import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Typography,
  message,
  Space,
  Modal,
} from "antd";
import styled, { css } from "styled-components";
import moment from "moment";
import container from "container/BrokerDashboard/editAgentContainer";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { LoadingOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null; // return integer if found, null if not found
};

const SuccessMessage = styled.div`
  border-radius: 8px;
  background: var(--Success-600, #16A34A);
  position: fixed;
  right: 79.5px;
  top: 84px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 5px;
`;
const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeleteIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 0;
`;

const CapsuleButton = styled(Button)`
  border-radius: 999px !important;
  border-width: 1px;
  padding: 5px 20px !important;
  font-weight: 500;
  font-size: 14px;
  min-height: 32px;
  ${({ active }) =>
    active
      ? `
    border-color: #6366f1 !important;
    color: #6366f1 !important;
    background: #ffffff !important;
  `
      : `
    border-color: #d9d9d9 !important;
    color: #595959 !important;
    background: #fff !important;
  `}
`;

const LICENSE_STATES = [
  "California",
  "Colorado",
  "Florida",
  "Oregon",
  "Texas",
  "Washington",
  "Georgia",
  "Arizona",
  "New York",
];

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #262626;
    font-weight: 500;
    font-size: 14px;
  }
  
  .ant-input, .ant-picker {
    border: 1px solid #d9d9d9;
    border-radius: 8px !important;
    background: #ffffff;
    color: #262626;
    height: 40px !important;
    
    &:hover {
      border-color: #bfbfbf;
    }
    
    &:focus {
      border-color: #722ed1;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
    }
  }
  
  .ant-input[disabled], .ant-picker-disabled {
    background: #ffffff;
    color: #262626;
    border-color: #d9d9d9;
  }
  
  .ant-picker {
    height: 40px !important;
    border-radius: 8px !important;
    
    .ant-picker-input > input {
      height: 38px;
      line-height: 38px;
      border-radius: 8px;
    }
  }
  
  .ant-picker-disabled {
    background: #ffffff !important;
    color: #262626 !important;
    border-color: #d9d9d9 !important;
    
    .ant-picker-input > input {
      background: #ffffff !important;
      color: #262626 !important;
    }
    
    .ant-picker-suffix {
      color: #bfbfbf !important;
    }
  }
  
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  
  margin-bottom: 0 !important;
`;

const Card = styled.div`
  border: 1px solid #e0e0e0;
  padding: 20px;
  border-radius: 8px;
  margin-top: 16px;
  background: #fff;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  
  .ant-form {
    margin-bottom: 0 !important;
  }
  
  .ant-form-item {
    margin-bottom: 0 !important;
  }
  
  .ant-typography {
    font-weight: 500 !important;
    font-size: 16px !important;
    color: #262626 !important;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 480px;
  max-width: 90vw;
  padding: 0;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: none;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #262626;
  line-height: 1.4;
`;

const ModalBody = styled.div`
  padding: 0 24px 24px 24px;
  font-size: 14px;
  line-height: 1.6;
  color: #595959;
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button`
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  height: 40px;
  padding: 0 20px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s;
  
  &.cancel {
    background: #ffffff;
    border-color: #722ed1;
    color: #722ed1;
    
    &:hover {
      background: #f8f5ff;
      border-color: #722ed1;
      color: #722ed1;
    }
  }
  
  &.delete {
    background: #ff4d4f;
    border-color: #ff4d4f;
    color: #ffffff;
    
    &:hover {
      background: #ff7875;
      border-color: #ff7875;
      color: #ffffff;
    }
  }
`;

const LicenseSection = ({
  getOnboardingLicenseInfoResponse,
  updateOnboardingInfo,
  getOnboardingLicenseInfo,
}) => {
  const licensesData = Array.isArray(getOnboardingLicenseInfoResponse?.data)
    ? getOnboardingLicenseInfoResponse.data
    : [];

  const [formList, setFormList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    console.log("showSuccessMessage changed to:", showSuccessMessage);
    if (showSuccessMessage) {
      console.log("Setting timer to hide success message");
      const timer = setTimeout(() => {
        console.log("Timer expired - hiding success message");
        setShowSuccessMessage(false);
      }, 3000);
      return () => {
        console.log("Clearing timer");
        clearTimeout(timer);
      };
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    if (!Array.isArray(licensesData)) return;

    const normalized = licensesData.map((item) => ({
      ...item,
      license_expire_at: moment.unix(item.license_expire_at),
    }));

    setFormList(normalized);
  }, [JSON.stringify(licensesData)]);

  const handleChange = (index, key, value) => {
    const updatedList = [...formList];
    updatedList[index][key] = value;
    setFormList(updatedList);
  };

  const handleSave = () => {
    setIsSaving(true);
    const payload = {
      step_completed: "license_info",
      agent_id: getAgentIdFromUrl(),
      data: formList.map((item) => ({
        state: item.state,
        realEstateLicenseNumber: item.real_estate_license_number,
        licenseExpireAt: item.license_expire_at.unix(),
      })),
    };
    
    console.log("About to call updateOnboardingInfo");
    
    updateOnboardingInfo({
      payload: payload,
      callback: () => {
        console.log("Callback executed - setting success message");
        // Refresh license info data after update
        getOnboardingLicenseInfo({ agentId: getAgentIdFromUrl(), step: "license_info" });
        setIsEditing(false);
        setIsSaving(false);
        setShowSuccessMessage(true);
        console.log("Success message set to true");
      },
    });

    console.log("Saved Payload:", payload);
  };

  const handleDelete = (index) => {
    setDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      const state = formList[deleteIndex].state;
      const updated = [...formList];
      updated.splice(deleteIndex, 1);
      setFormList(updated);
      message.success(`${state} license removed`);
      setShowDeleteModal(false);
      setDeleteIndex(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };

  const handleAddLicenseForState = (state) => {
    if (formList.find((f) => f.state === state)) {
      message.warning(`${state} license already added`);
      return;
    }

    setFormList([
      ...formList,
      {
        agent_id: getAgentIdFromUrl(),
        id: null,
        state,
        real_estate_license_number: "",
        license_expire_at: moment(),
      },
    ]);
  };

  return (
    <div>
      {console.log("Rendering LicenseSection, showSuccessMessage:", showSuccessMessage)}
      {showSuccessMessage && (
        <SuccessMessage>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Onboarding details has been updated
        </SuccessMessage>
      )}
      <SectionHeader>
        {/* <Title level={4}>License Information</Title> */}

        <HeaderContent>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.75 9.75C6.75 9.33579 7.08579 9 7.5 9H16.5C16.9142 9 17.25 9.33579 17.25 9.75C17.25 10.1642 16.9142 10.5 16.5 10.5H7.5C7.08579 10.5 6.75 10.1642 6.75 9.75ZM7.5 13.5H16.5C16.9142 13.5 17.25 13.1642 17.25 12.75C17.25 12.3358 16.9142 12 16.5 12H7.5C7.08579 12 6.75 12.3358 6.75 12.75C6.75 13.1642 7.08579 13.5 7.5 13.5ZM21.75 5.25V19.5C21.7499 19.7599 21.6152 20.0012 21.394 20.1378C21.1729 20.2743 20.8968 20.2866 20.6644 20.1703L18 18.8381L15.3356 20.1703C15.1244 20.276 14.8756 20.276 14.6644 20.1703L12 18.8381L9.33562 20.1703C9.12436 20.276 8.87564 20.276 8.66438 20.1703L6 18.8381L3.33562 20.1703C3.1032 20.2866 2.82712 20.2743 2.60597 20.1378C2.38483 20.0012 2.25014 19.7599 2.25 19.5V5.25C2.25 4.42157 2.92157 3.75 3.75 3.75H20.25C21.0784 3.75 21.75 4.42157 21.75 5.25ZM20.25 5.25H3.75V18.2869L5.66437 17.3288C5.87564 17.223 6.12436 17.223 6.33563 17.3288L9 18.6619L11.6644 17.3288C11.8756 17.223 12.1244 17.223 12.3356 17.3288L15 18.6619L17.6644 17.3288C17.8756 17.223 18.1244 17.223 18.3356 17.3288L20.25 18.2869V5.25Z"
              fill="#111827"
            />
          </svg>
          <SectionTitle>License Information</SectionTitle>
        </HeaderContent>
        {!isEditing ? (
          <Button
            type="default"
            style={{
              border: '1px solid #6366F1',
              borderRadius: '8px',
              background: '#6366F1',
              color: 'white',
              fontWeight: 500,
              height: '36px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={() => setIsEditing(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.41421 3.25253C6.19526 4.03358 6.19526 5.29991 5.41421 6.08096C4.63316 6.86201 3.36683 6.86201 2.58579 6.08096C1.80474 5.29991 1.80474 4.03358 2.58579 3.25253C3.36683 2.47149 4.63316 2.47149 5.41421 3.25253" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.3333 4.66659H6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.4142 9.91904C14.1953 10.7001 14.1953 11.9664 13.4142 12.7475C12.6332 13.5285 11.3668 13.5285 10.5858 12.7475C9.80474 11.9664 9.80474 10.7001 10.5858 9.91904C11.3668 9.13799 12.6332 9.13799 13.4142 9.91904" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.6665 11.3333H9.99984" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit
          </Button>
        ) : (
          <Space>
            <Button type="text" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button 
              type="default"
              disabled={isSaving}
              style={{
                borderRadius: '8px',
                border: '1px solid #6366F1',
                background: '#6366F1',
                boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.04), 0 1px 2px 0 rgba(16, 24, 40, 0.04)',
                color: 'white',
                fontWeight: 500,
                height: '36px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={handleSave}
            >
              {isSaving ? (
                <>
                  <LoadingOutlined style={{ color: "white", marginRight: "8px" }}  />
                  Saving...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: "8px", position: "relative", top: "3px"}}>
                    <path d="M10.6663 12.6667H11.9997C13.4724 12.6667 14.6663 11.4728 14.6663 10V10C14.6663 8.52725 13.4724 7.33334 11.9997 7.33334V7.33334C11.9997 5.1242 10.2088 3.33334 7.99967 3.33334V3.33334C6.2878 3.33418 4.76634 4.42462 4.21554 6.04546C2.56745 6.26593 1.33598 7.67057 1.33301 9.33334H1.33301C1.33301 11.1743 2.82539 12.6667 4.66634 12.6667H5.33301" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.33366 10.6667L8.00033 9.33334L6.66699 10.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8.00033 12.6667V9.33334" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Save
                </>
              )}
            </Button>
          </Space>
        )}
      </SectionHeader>

      {isEditing && (
        <Space wrap style={{ marginBottom: 12 }}>
          {LICENSE_STATES.map((state) => (
            <CapsuleButton
              key={state}
              type="default"
              active={!!formList.find((f) => f.state === state)}
              onClick={() => handleAddLicenseForState(state)}
              size="small"
            >
              {state}
            </CapsuleButton>
          ))}
        </Space>
      )}

      {!isEditing && (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            {LICENSE_STATES.map((state) => (
              <CapsuleButton
                key={state}
                type="default"
                active={!!formList.find((f) => f.state === state)}
                size="small"
                disabled
              >
                {state}
              </CapsuleButton>
            ))}
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Click Edit to add or remove license information for different
            states.
          </Text>
        </>
      )}

      {formList.map((license, index) => (
        <Card key={license.id || index}>
          {isEditing && (
            <DeleteIconWrapper>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(index)}
              />
            </DeleteIconWrapper>
          )}

          <Title level={5} style={{ marginBottom: 8, fontWeight: 500, fontSize: 16, marginLeft: "3px", marginBottom: "10px"}}>
            {license.state || "License"} license details
          </Title>

          <Form layout="vertical">
            <Grid>
              <StyledFormItem label="Real Estate License No.">
                <Input
                  value={license.real_estate_license_number}
                  onChange={(e) =>
                    handleChange(
                      index,
                      "real_estate_license_number",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                />
              </StyledFormItem>
              <StyledFormItem label="Expiration date">
                <DatePicker
                  style={{ width: '100%' }}
                  value={license.license_expire_at}
                  onChange={(date) =>
                    handleChange(index, "license_expire_at", date)
                  }
                  disabled={!isEditing}
                  format="MM/DD/YYYY"
                />
              </StyledFormItem>
            </Grid>
          </Form>
        </Card>
      ))}

      {/* Custom Delete Modal */}
      {showDeleteModal && deleteIndex !== null && (
        <ModalOverlay onClick={cancelDelete}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                Remove {formList[deleteIndex]?.state} License
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              Are you sure you want to remove the {formList[deleteIndex]?.state} license information? This will permanently delete all license details for this state and cannot be undone.
            </ModalBody>
            <ModalFooter>
              <ModalButton className="cancel" onClick={cancelDelete}>
                Cancel
              </ModalButton>
              <ModalButton className="delete" onClick={confirmDelete}>
                Delete
              </ModalButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </div>
  );
};

export default container(LicenseSection);
